import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getParticipantManagementInboxData, postAddedParticipantReview, postUpdatedParticipantReview } from "../../redux/slices/participant-management-inbox-slice";
import { AddUpdateParticipantModal, BackButton, BadgeComponent, Breadcrumbs, DropDownComponent, IconButtonComponent, LoaderComponent, PaginationComponent } from "../../components";
import { VerticalDotsSvg } from "../../assets/svgs";
import { getParticipantsLogoDetail, getParticipantsMessageInfo } from "../../redux/slices/participants-slice";
import { AuthPageContainer, AuthPageHead, ReactTableWrapper, ReactTableWrappers } from "../../styles/global-styles";
import { getInstitutionsList } from "../../redux/slices/institutions-list-slice";
import { useLocation } from "react-router";
import { Menus } from "../../constants/menus-constant";
import ReactTable from "react-table";

const ParticipantManagementInbox = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.loginUserReducer);
    const { loading, inboxData, pagination } = useSelector((state) => state.participantManagementInboxReducer);

    const MenuList = Menus({ user });
    
    const isChecker = location.pathname.includes('checker'); // Check if the current path includes 'checker'
    const { label: PageLabel, icon: PageIcon } = MenuList?.flatMap(i => i?.subMenus)?.filter(i => i?.to === location?.pathname)[0];

    const initialPagination = {
        currentPage: 0,
        pageSize: 20,
    };

    const initialStates = {
        data: [],
        pagination: initialPagination,
        selected: null,
        isDropdownOpen: false,
        isModalOpen: false,
        isUpdate: true,
    };

    const [states, setStates] = useState(initialStates);
    const [form, setForm] = useState();

    const defaultParams = { pagination: states.pagination };

    const getInboxData = async (updatedParams) => {
        if (user && updatedParams) {
            try {
                await Promise.all([
                    dispatch(getParticipantManagementInboxData({ user, ...updatedParams })),
                    dispatch(getParticipantsLogoDetail(user)),
                    dispatch(getParticipantsMessageInfo(user))
                ]);
            } catch (error) {
                console.error("Error in one or more API calls", error);
            }
        }
    };

    // const handleReload = () => {
    //     getInboxData(defaultParams);
    // };

    const handlePagination = (e) => {
        const params = {
            ...defaultParams,
            pagination: {
                ...defaultParams.pagination,
                currentPage: e
            }
        };
        getInboxData(params);
    };

    const handleSelectRow = (row) => {
        if (row === states.selected && states.isDropdownOpen) {
            setStates((old) => ({ ...old, isDropdownOpen: false, selected: null }));
        } else {
            setStates((old) => ({ ...old, isDropdownOpen: true, selected: row }));
        }
    };

    const handleCloseDropdown = () => {
        setStates((old) => ({ ...old, isDropdownOpen: false }));
    };

    const handleModalOpen = () => {
        setForm({
            ...states?.selected?.taskDetailRes,
            authenticationTypeId: states?.selected?.taskDetailRes?.authenticationTypeId || 1,
            customerCanSelectAuthMethod: states?.selected?.taskDetailRes?.customerCanSelectAuthMethod,
            isMultiLanguageSupport: states?.selected?.taskDetailRes?.isMultiLanguageSupport,
            reason: states?.selected?.reason
        });
        setStates((old) => ({ ...old, isModalOpen: true }));
    };

    const handleModalClose = () => {
        setStates((old) => ({ ...old, isModalOpen: false, selected: null }));
    };

    const handleFormSubmit = (payloads) => {
        const { operation, processId } = states.selected;
        const { addUpdateParticipantRequest, ...payload } = payloads;

        const currentOperation = operation?.toLowerCase();
        const isUpdateOperation = ['participant deactivation', 'participant activation', 'participant updation'].includes(currentOperation);

        const updatedData = {
            ...payload,
            operation,
            processId,
            ...(isUpdateOperation
                ? { updateParticipantRequest: addUpdateParticipantRequest }
                : { addParticipantRequest: addUpdateParticipantRequest })
        };

        const actionCreator = isUpdateOperation
            ? postUpdatedParticipantReview
            : postAddedParticipantReview;

        dispatch(actionCreator({ user, body: updatedData })).then((action) => {
            if (action?.payload?.status === 201 || action?.payload?.status === 200) {
                handleModalClose();
                getInboxData(defaultParams);
                dispatch(getInstitutionsList(user));
            }
        });
    };

    const actions = [
        { label: isChecker ? 'Review' : 'Edit', action: () => handleModalOpen() },
    ];

    useEffect(() => {
        getInboxData(defaultParams);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (inboxData && Array.isArray(inboxData)) {
            setStates((old) => ({ ...old, data: inboxData, pagination: pagination }));
        }
    }, [inboxData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown')) {
                handleCloseDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // TABLE COLUMNS SETUP AS PER DATA
    // const columns = [
    //     { field: 'processId', headerName: 'Process ID', width: 150 },
    //     {
    //         field: 'participantName', headerName: 'Participant Name', width: 130,
    //         valueGetter: (row) => row?.taskDetailRes?.participantName
    //     },
    //     {
    //         field: 'operation',
    //         headerName: 'Operation',
    //         width: 170,
    //         align: 'center',
    //         valueGetter: (row) => {
    //             const operation = row?.operation?.toLowerCase();
    //             let badgeType = 'default';

    //             if (operation?.includes('deactivation')) {
    //                 badgeType = 'error';
    //             } else if (operation?.includes('activation')) {
    //                 badgeType = 'success';
    //             } else if (operation?.includes('updation')) {
    //                 badgeType = 'warning';
    //             }

    //             return (
    //                 <>
    //                     {
    //                         row?.operation &&
    //                         <BadgeComponent label={row?.operation} outline type={badgeType} />
    //                     }
    //                 </>
    //             );
    //         },
    //         sortBy: (row) => row?.operation,
    //     },
    //     { field: 'createdBy', headerName: 'Created / Modified By', width: 160 },
    //     {
    //         field: 'isaccepted',
    //         headerName: 'Status',
    //         width: 120,
    //         align: 'center',
    //         valueGetter: (row) => {
    //             return (
    //                 <>
    //                     {
    //                         ([true, false].includes(row?.isaccepted) && row?.reason) ? (
    //                             <BadgeComponent label={row?.isaccepted ? 'Accepted' : 'Rejected'} type={row?.isaccepted ? 'success' : 'error'} />
    //                         ) : (
    //                             <BadgeComponent label='Pending' type='default' />
    //                         )
    //                     }
    //                 </>
    //             )
    //         },
    //         // sortBy: (row) => row.userStatus.label,
    //     },
    //     { field: 'reason', headerName: 'Comments', width: 140 },
    //     { field: 'reqInitDateTime', headerName: 'Request Date', width: 170, align: 'center' },
    //     {
    //         field: 'action',
    //         headerName: 'Action',
    //         sortable: false,
    //         width: 100,
    //         align: 'right',
    //         sticky: 'right',
    //         valueGetter: (row) => {
    //             return (
    //                 <div className="action-dropdown" style={{ position: 'relative', float: 'right' }}>
    //                     <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow(row)} />
    //                     <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row} close={handleCloseDropdown} actions={actions} />
    //                 </div>
    //             )
    //         },
    //     },
    // ];


    const columns = useMemo(() => [
          {
            accessor: 'reqInitDateTime',
            Header: 'Request Date',
            minWidth: 170,
            style: {
                textAlign: 'center'
            },
            sortable: false,
            filterable: false
        },
        {
            accessor: 'processId', Header: 'Process ID', minWidth: 160,
            style: {
                textAlign: 'center'
            },
            sortable: false, filterable: false
        },
        {
            accessor: 'participantName',
            Header: 'Participant Name',
            minWidth: 140,
            style: {
                textAlign: 'center'
            },
            Cell: (row) => row?.original?.taskDetailRes?.participantName,
            sortable: false,
            filterable: false
        },
        {
            accessor: 'operation',
            Header: 'Operation',
            minWidth: 160,
            sortable: false,
            filterable: false,
            style: {
                textAlign: 'center'
            },
            Cell: (row) => {
                const operation = row?.original?.operation?.toLowerCase();
                let badgeType = 'default';

                if (operation?.includes('deactivation')) {
                    badgeType = 'error';
                } else if (operation?.includes('activation')) {
                    badgeType = 'success';
                } else if (operation?.includes('updation')) {
                    badgeType = 'warning';
                }

                return (
                    <>
                        {
                            row?.original?.operation &&
                            <BadgeComponent label={row?.original?.operation} type={badgeType} />
                        }
                    </>
                );
            },
        },
        {
            accessor: 'createdBy', Header: 'Created / Modified By', minWidth: 160,
            style: {
                textAlign: 'center'
            },
            sortable: false, filterable: false
        },
        {
            accessor: 'isaccepted',
            Header: 'Status',
            minWidth: 120,
            style: {
                textAlign: 'center'
            },
            Cell: (row) => {
                return (
                    <>
                        {
                            ([true, false].includes(row?.isaccepted) && row?.reason) ? (
                                <BadgeComponent label={row?.isaccepted ? 'Accepted' : 'Rejected'} type={row?.isaccepted ? 'success' : 'error'} />
                            ) : (
                                <BadgeComponent label='Pending' type='warning' />
                            )
                        }
                    </>
                )
            },
            sortable: false, filterable: false
        },
        {
            accessor: 'reason', Header: 'Comments', minWidth: 120,
            style: {
                textAlign: 'center'
            },
            sortable: false, filterable: false
        },
        {
            accessor: 'action',
            Header: 'Action',
            minWidth: 100,
            // style: {
            //     height: 45, display: 'flex', justifyContent: 'flex-end', alignItems: 'center'
            // },
            Cell: (row) => {
                return (
                    <div className="action-dropdown" style={{ position: 'absolute' }}>
                        <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row?.original)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow(row?.original)} />
                        <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row?.original} close={handleCloseDropdown} actions={actions} />
                    </div>
                )
            },
            sortable: false,
            filterable: false,
        },
    ], [ states.isDropdownOpen, states.selected ]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AuthPageContainer $heightfull>
            {/* BACK BUTTON */}
            <BackButton />
            {/* PAGE HEAD */}
            <AuthPageHead>
                <div className="left">
                    <PageIcon className="page-icon" />
                    <h1>Inbox</h1>
                    {/* <IconButtonComponent icon={ReloadSvg} outline size="small" disabled={loading} onClick={handleReload} className={loading && 'box-spin'} /> */}
                    <Breadcrumbs
                        items={[
                            { label: "Home", to: "/app/home" },
                            { label: PageLabel },
                        ]}
                        separator="›"
                        activeLastBreadcrumb={false}
                        size="small"
                        // color={'var(--text-color-secondary)'}
                        color={'var(--text-color-primary)'}
                    />
                </div>
            </AuthPageHead>

            {/* LOADING STATE */}
            {
                loading &&
                <LoaderComponent />
            }

            {/* EMPTY STATE */}
            {/* {
                !loading && states.data && states.data.length === 0 &&
                <EmptyTableComponent text='No data found!' />
            } */}

            {/* PAGE CONTENT */}
            {
                // !loading && states.data && states.data.length > 0 &&
             <>
                   <PaginationComponent
                        page={states?.pagination?.currentPage}
                        pages={states?.pagination?.totalPages || 1}
                        onPageChange={(e) => handlePagination(e)}
                        canPrevious={states?.pagination?.currentPage > 0}
                        canNext={states?.pagination?.currentPage < (states?.pagination?.totalPages - 1)}
                    />
                    <ReactTableWrapper className="box-shadow custom-scrollbar">
                        {/* <TableComponent rows={states.data} columns={columns} icon={BadgeSvg} />
                    <PaginationComponent data={states.pagination} handleChange={(e) => handlePagination(e)} /> */}
                        <ReactTableWrappers>
                            <ReactTable
                                manual
                                loading={loading}
                                data={states?.data}
                                columns={columns}
                                page={states?.pagination?.currentPage || 0}
                                pageSize={states?.pagination?.pageSize || 20}
                                pages={states?.pagination?.totalPages || 1}
                                filterable={false}
                                showPagination={false}
                                showPageJump={true}
                                showPageSizeOptions={false}
                                showPaginationTop={true}
                                showPaginationBottom={false}
                                onPageChange={(e) => handlePagination(e)}
                                minRows={0}
                            // defaultFilterMethod={(filter, row, column) => {
                            //     return true;
                            // }}
                            // onFilteredChange={(filtered, column, value) => {
                            //     const searchCriteria = transformToObject(filtered);
                            //     setComponentState((prevState) => ({
                            //         ...prevState,
                            //         searchCriteria,
                            //         tableLoading: true,
                            //     }));

                            //     handleOnSearch(searchCriteria); // Trigger handle search
                            // }}
                            />
                        </ReactTableWrappers>
                    </ReactTableWrapper>
                </>
            }

            {/* MODAL */}
            {
                states.isModalOpen &&
                <AddUpdateParticipantModal data={form} label={states?.selected?.operation} update={states.isUpdate} close={handleModalClose} handleFormSubmit={handleFormSubmit} fromMakerChecker slide/>
            }
        </AuthPageContainer>
    );
};

export default ParticipantManagementInbox;